import React, {useEffect, useRef, useState} from 'react';
import {FieldProp, Field, FieldConditionalLogic, FieldConditionalLogicRule} from '../../types';
import {Modal} from '../Modal';
import {ActivityButton} from '../ActivityButton';
import {Form, FormState, initialFormState} from '../Form';
import {fieldValueLabel, formIsBusy, validate, parseMergeTag, fieldValueText, applyVisibilityRules, fieldValue} from '../../_helpers/form';
import {FormContainer} from '../FormContainer';
import {NestedFormExcelUpload} from './NestedFormExcelUpload';
import useDetectPrint from '../../_hooks/detectPrint';
import {Alert} from '../Alert';

type NestedFormProps = FieldProp & {
    additionalButtons: JSX.Element[] | undefined;   // additional list of buttons to display next to "Add ..."
                                                    // it is the parent's responsibility to handle any button interactions
};

const initialAddRecordModalMessageState = {
    show: false
}

export const NestedForm: React.FunctionComponent<NestedFormProps> = ({
                                                                         field,
                                                                         fieldState,
                                                                         displayOnly = false,
                                                                         value,
                                                                         onChange,
                                                                         additionalButtons,
                                                                         showAdministrativeFields = false,
                                                                         ...rest
                                                                     }): JSX.Element => {

    const initialFieldValues = field.nestedForm.fields.reduce((acc: any, field: Field) => {
        const {form, formState} = rest;

        // @ts-ignore
        return {...acc, [field.name]: parseMergeTag(field.initialValue, form, formState)}
    }, {});

    const [formState, setFormState] = useState<FormState>({
        ...initialFormState,
        fieldValues: initialFieldValues
    });

    const [isShowingButtonMenu, setIsShowingButtonMenu] = useState(false);
    const buttonDropdownRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            // @ts-ignore
            if (buttonDropdownRef?.current && !buttonDropdownRef.current.contains(event.target)) {
                setIsShowingButtonMenu(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [buttonDropdownRef]);

    // get visibility state of current field value
    const valueVisibility = value?.map((fieldValue: any) => (
        applyVisibilityRules(field.nestedForm, undefined, {
            ...initialFormState,
            fieldValues: fieldValue
        }, field?.gpnfListAdministrativeFields || showAdministrativeFields, false)
    ));

    const [formIsSubmitting, setFormIsSubmitting] = useState<boolean>(false);
    const [showAddRecordModal, setShowAddRecordModal] = useState<boolean>(false);
    const [showExcelUploadModal, setShowExcelUploadModal] = useState<boolean>(false);
    const [nestedFormExcelUploadErrorMessage, setNestedFormExcelUploadErrorMessage] = useState('');

    const [addRecordModalMessage, setAddRecordModalMessage] = useState<any>({...initialAddRecordModalMessageState});

    const hasExcelUpload = (field?.flowCustomDisplayComponentConfig?.hasOwnProperty('excelUpload'));
    const hasCsvDownload = (field?.flowCustomDisplayComponentConfig?.hasOwnProperty('csvDownload'));
    const hideAddButton = field?.flowCustomDisplayComponentConfig?.buttons?.add?.hide || field?.gpnfDisableAddNewRecordButton || false;

    const addRecordModalSize = field?.flowCustomDisplayComponentConfig?.addRecordModal?.size || 'default';

    const viewButtonCss = field?.flowCustomDisplayComponentConfig?.buttons?.view?.cssClasses || 'text-secondary';
    const viewButtonLabel = field?.flowCustomDisplayComponentConfig?.buttons?.view?.label || 'view';
    const viewButtonShouldLinkToEntry = field?.flowCustomDisplayComponentConfig?.buttons?.view?.shouldLinkToEntry || false;
    const viewButtonShouldLinkToEntryPrefix = field?.flowCustomDisplayComponentConfig?.buttons?.view?.linkToEntryPrefix || '/p/flow/entry/';

    const editButtonCss = field?.flowCustomDisplayComponentConfig?.buttons?.edit?.cssClasses || 'text-secondary';
    const editButtonLabel = field?.flowCustomDisplayComponentConfig?.buttons?.edit?.label || field?.gpnfEditButtonLabel || 'edit';
    const deleteButtonCss = field?.flowCustomDisplayComponentConfig?.buttons?.delete?.cssClasses || 'text-danger';
    const deleteButtonLabel = field?.flowCustomDisplayComponentConfig?.buttons?.delete?.label || field?.gpnfDeleteButtonLabel || 'delete';

    const editAndDeleteButtonPosition = field?.flowCustomDisplayComponentConfig?.buttons?.editAndDeleteButtonPosition || 'end';

    const saveOptions = field?.flowCustomDisplayComponentConfig?.saveOptions || [];

    const {
        touched,
        pristine,
        validating,
        valid,
        invalid,
        errors,
    } = fieldState;

    const classNames = [
        touched && 'is-touched',
        pristine && 'is-pristine',
        validating && 'is-validating',
        valid && 'is-valid',
        invalid && 'is-invalid',
    ]
        .filter(Boolean)
        .join(' ');

    const disableEdit = field.hasOwnProperty('gpnfDisableEdit') ? field.gpnfDisableEdit : displayOnly;
    const disableDelete = (field.hasOwnProperty('gpnfDisableDelete') && field.gpnfDisableDelete === true);
    const localDisplayOnly = (disableEdit && disableDelete);
    const modalLocalDisplayOnly = disableEdit;
    const isPrinting = useDetectPrint();

    console.log('NestedForm: displayOnly', displayOnly, ' disableEdit=', disableEdit, ' disableDelete=', disableDelete);
    console.log('NestedForm: field ', field);

    const handleCsvDownload = (field: Field, value: any) => {
        const csv: any = [];

        // add the headers
        const headers: any = field?.flowCustomDisplayComponentConfig?.csvDownload?.columns.map((column: any): any => {
            return '"' + field.nestedForm.fieldMap[column].label
                .replace(/(\r\n|\n|\r)/gm, '')
                .replace(/(\s\s)/gm, ' ')
                .replace(/"/g, '""') + '"';
        });
        csv.push(headers.join(','));

        const rows: any = value.map((item: any) => {
            const row = field?.flowCustomDisplayComponentConfig?.csvDownload?.columns.map((column: any): any => {
                return '"' + item['input_' + column]
                    .replace(/(\r\n|\n|\r)/gm, '')
                    .replace(/(\s\s)/gm, ' ')
                    .replace(/"/g, '""') + '"';
            });
            return row.join(',');
        });
        csv.push(...rows);

        const csvString = csv.join('\n');
        const filename = field.nestedForm.title + '.csv';
        const link = document.createElement('a');
        link.style.display = 'none';
        link.setAttribute('target', '_blank');
        link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvString));
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // NOTE - this function is heavily dependent on variables defined in the component scope
    //        they should be passed as variables
    const renderEditAndDeleteButtons = (item: any, index: number) => {
        return (
            <td style={{whiteSpace: 'nowrap'}}>
                <div>
                    {disableEdit &&
                        <>
                            {viewButtonShouldLinkToEntry ?
                                <>
                                    <a
                                        href={`${viewButtonShouldLinkToEntryPrefix}${item.id}`}
                                        className={`${viewButtonCss} mr-2`}
                                    >{viewButtonLabel}</a>
                                </>
                                :
                                <a href="#"
                                   className={`${viewButtonCss} mr-2`}
                                   onClick={(e) => {
                                       e.preventDefault();

                                       setFormState({
                                           ...initialFormState,
                                           fieldState: {},
                                           fieldValues: {...item, __index: index}
                                       });

                                       setShowAddRecordModal(true);
                                   }}>{viewButtonLabel}</a>
                            }
                        </>
                    }

                    {(!item.id || (item.id && (item?.__fe_allow_edit === true || field?.gpnfEditableFields?.length))) ?
                        <>
                            {(disableEdit === false || (disableEdit === true && !item.id)) &&
                                <a href="#"
                                   className={`${editButtonCss} mr-2`}
                                   onClick={(e) => {
                                       e.preventDefault();

                                       setFormState({
                                           ...initialFormState,
                                           fieldState: {},
                                           fieldValues: {...item, __index: index}
                                       });

                                       setShowAddRecordModal(true);
                                   }}>{editButtonLabel}</a>
                            }
                            {(disableDelete === false || (disableDelete === true && !item.id)) &&
                                <a href="#"
                                   className={`${deleteButtonCss}`}
                                   onClick={(e) => {
                                       e.preventDefault();

                                       onChange && onChange([
                                           ...value.slice(0, index),
                                           ...value.slice(index + 1),
                                       ]);
                                   }}
                                >{deleteButtonLabel}</a>
                            }
                        </>
                        :
                        <></>
                    }
                </div>
            </td>
        )
    }

    const handleSaveNestedFormEntry = (): boolean => {
        // @ts-ignore
        const filterFields: any = field?.gpnfEditableFields?.length > 0 ? field.gpnfEditableFields.map((field: any) => parseInt(field, 10).toString()) : [];
        if (!validate(field.nestedForm, formState, setFormState, filterFields)) {
            setFormIsSubmitting(false);
            return false;
        }
        // set the new stated for the nested form, if this is a new record,
        // take current form state to end, if it is an update, replace
        // edited item with current form state
        const newState = (formState.fieldValues.hasOwnProperty('__index')) ?
            [
                ...value.slice(0, formState.fieldValues.__index),
                formState.fieldValues,
                ...value.slice(formState.fieldValues.__index + 1),
            ]
            :
            [
                ...(value ? value : []),
                formState.fieldValues
            ];

        // add this record to parent form state
        onChange && onChange(newState);
        setFormState({
            ...initialFormState,
            fieldValues: initialFieldValues
        });

        return true;
    }

    const getLabelForSaveOption = (saveOption: string) => {
        switch (saveOption) {
            case 'save_and_new':
                return 'Save and New';
            case 'save_and_copy_new':
                return 'Save and Copy to New';
            default:
                return 'ERROR';
        }
    }

    // temp hack to display date fields in nested form list
    // not modifying fieldValueLabel for fear of impacting other components
    const formatDateField = (field: any, item: any) => {
        const format = field?.dateDisplayFormat || '';
        const v = fieldValue({...initialFieldValues, fieldValues: item}, field);
        if (v && v.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const parts: string[] = v.split('-');

            if (format === 'MMMM-dd-yyyy') {
                const monthNames = {
                    '01': 'January',
                    '02': 'February',
                    '03': 'March',
                    '04': 'April',
                    '05': 'May',
                    '06': 'June',
                    '07': 'July',
                    '08': 'August',
                    '09': 'September',
                    '10': 'October',
                    '11': 'November',
                    '12': 'December'
                };
                // @ts-ignore
                return [monthNames[parts[1]], parts[2], parts[0]].join('-');
            } else if (format === 'd MMM yyyy') {
                const monthNames = {
                    '01': 'Jan',
                    '02': 'Feb',
                    '03': 'Mar',
                    '04': 'Apr',
                    '05': 'May',
                    '06': 'Jun',
                    '07': 'Jul',
                    '08': 'Aug',
                    '09': 'Sep',
                    '10': 'Oct',
                    '11': 'Nov',
                    '12': 'Dec'
                };
                // @ts-ignore
                return [parts[2], monthNames[parts[1]], parts[0]].join(' ');
            } else {
                return [parts[1], parts[2], parts[0]].join('/');
            }
        }
        return v;
    }

    return (
        <div className={classNames}>

            {errors && errors.map((error: string, index: number) => (
                <div key={index} className="invalid-feedback">
                    {error}
                </div>
            ))}
            {nestedFormExcelUploadErrorMessage &&
                nestedFormExcelUploadErrorMessage
                    .split(/\n/)
                    .map((m: string, index: number) => (
                        <div key={index} className="invalid-feedback">
                            {m}
                        </div>
                    ))}

            {isPrinting ?
                <div className="nested-form-print-container">
                    {value.map((item: any, index: number) => (
                        <div>
                            <div key={index} className="row no-gutters entry-container">
                                {field.gpnfFields.map((fieldId: number) => {
                                    const nestedField = field.nestedForm.fieldMap[fieldId];
                                    return (
                                        <div className="col">
                                            <div className="card">
                                                <h6 className="field-title">{nestedField.label}</h6>
                                                <div className="card-body">
                                                    <p className="field-value">
                                                        {nestedField.type === 'date' ?
                                                            formatDateField(nestedField, item)
                                                            :
                                                            fieldValueLabel({
                                                                ...initialFormState,
                                                                fieldValues: item
                                                            }, field.nestedForm, fieldId)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                :
                <div className="nested-table-container">
                    <table className="table table-sm">
                        <thead>
                        <tr>
                            {editAndDeleteButtonPosition === 'start' && <th/>}
                            {field?.gpnfFields?.map((fieldId: any, index: number) => {
                                const nestedField: Field = field.nestedForm.fieldMap.hasOwnProperty(fieldId) ? field.nestedForm.fieldMap[fieldId] : null;
                                const label = nestedField?.flowCustomDisplayComponentConfig?.tableHeadingLabelText || nestedField.label;

                                // adv option always overrides
                                const gpnfListAdminFields = field?.gpnfListAdministrativeFields || false;
                                if (!(nestedField.visibility === 'administrative' && gpnfListAdminFields)) {
                                    if (nestedField.visibility === 'hidden' || (nestedField.visibility === 'administrative' && !showAdministrativeFields)) {
                                        return null;
                                    }
                                }

                                // if all fields in all rows have visibility of hidden, then hide the column
                                const fieldValueKey = 'input_' + nestedField.id;
                                const allHidden = valueVisibility.every((value: any) => value.hasOwnProperty(fieldValueKey) && value[fieldValueKey] === false);
                                if (allHidden) {
                                    return null;
                                }

                                // conditionally show columns
                                // TODO: this code is duplicated from from.tsx, should be refactored into a common function
                                if (field?.flowCustomDisplayComponentConfig?.conditionalColumnDisplay) {
                                    const conditionalLogic = field.flowCustomDisplayComponentConfig.conditionalColumnDisplay;
                                    if (Array.isArray(conditionalLogic) && conditionalLogic.length > 0) {
                                        let flowLogicRuleMatch = false;
                                        let shouldShow = true; // by default, we will show the column
                                        conditionalLogic.forEach((logic: FieldConditionalLogic) => {
                                            if (logic?.columns?.includes(fieldId)) {
                                                if (logic?.rules?.length > 0) {
                                                    const conditionalLogic: FieldConditionalLogic = logic;
                                                    // console.log('flowConditionalLogic', conditionalLogic);
                                                    let matchCount = 0;
                                                    conditionalLogic.rules.forEach((rule: FieldConditionalLogicRule) => {
                                                        const targetFieldValue = fieldValueText(rest.formState, rest.form, rule.fieldId);

                                                        if (rule.operator === 'is' && targetFieldValue === rule.value) {
                                                            matchCount++;
                                                        } else if (rule.operator === 'contains' && typeof targetFieldValue === 'string' && (new RegExp(rule.value, 'i')).test(targetFieldValue as string)) {
                                                            matchCount++;
                                                        } else if (rule.operator === 'isnot' && targetFieldValue !== rule.value) {
                                                            matchCount++;
                                                        } else if ((rule.operator === '>' || rule.operator === '<') && targetFieldValue && !(targetFieldValue instanceof Array)) {
                                                            // @ts-ignore
                                                            const numberValue = targetFieldValue.replace(/[$,]/g, '');

                                                            if (
                                                                (rule.operator === '>' && numberValue > rule.value) ||
                                                                (rule.operator === '<' && numberValue < rule.value)
                                                            ) {
                                                                matchCount++;
                                                            }
                                                        }
                                                    });

                                                    const ruleTrue = (
                                                        (conditionalLogic.logicType === 'all' && matchCount === conditionalLogic.rules.length) ||
                                                        (conditionalLogic.logicType === 'any' && matchCount > 0)
                                                    );

                                                    flowLogicRuleMatch = flowLogicRuleMatch || (ruleTrue && conditionalLogic.actionType === 'show');
                                                }

                                                shouldShow = shouldShow && flowLogicRuleMatch;
                                            }
                                        });

                                        if (!shouldShow) {
                                            return null;
                                        }
                                    }

                                }

                                return <th key={index}>{label}</th>;
                            })}
                            {editAndDeleteButtonPosition === 'end' && <th/>}
                        </tr>
                        </thead>
                        {/* the ternary operator while dumb, removes the "empty text node in table" warning thrown by react: https://stackoverflow.com/a/53519842 */}
                        {value && value.length > 0 ? <tbody>
                        {value.map((item: any, index: number) => (
                            <tr key={index}>
                                {editAndDeleteButtonPosition === 'start' && renderEditAndDeleteButtons(item, index)}
                                {field.gpnfFields.map((fieldId: number) => {
                                    const nestedField = field.nestedForm.fieldMap[fieldId];

                                    // if all fields in all rows have visibility of hidden, then the columns will be hidden so hide the cell
                                    const fieldValueKey = 'input_' + nestedField.id;
                                    const allHidden = valueVisibility.every((value: any) => value.hasOwnProperty(fieldValueKey) && value[fieldValueKey] === false);
                                    if (allHidden) {
                                        return null;
                                    }

                                    // if specific field is hidden, return empty td
                                    if (value[index] && value[index].hasOwnProperty(fieldValueKey) && value[index][fieldValueKey] === false) {
                                        return <td/>;
                                    }

                                    // if field visibility is hidden, return empty td
                                    if (
                                        valueVisibility[index].hasOwnProperty(fieldValueKey) &&
                                        valueVisibility[index][fieldValueKey] === false
                                    ) {
                                        return <td/>;
                                    }

                                    return (
                                        <td key={`${index}_${nestedField.id}`} className={`field-${nestedField.type}`}
                                            /* fieldValueText expects state, make item look like state */
                                        >{nestedField.type === 'date' ?
                                            formatDateField(nestedField, item)
                                            :
                                            fieldValueLabel({
                                            ...initialFormState,
                                            fieldValues: item
                                        }, field.nestedForm, fieldId)}</td>
                                    );
                                })}

                                {editAndDeleteButtonPosition === 'end' && renderEditAndDeleteButtons(item, index)}
                            </tr>
                        ))}
                        </tbody> : null}
                    </table>
                </div>
            }
            {!(value && value.length > 0) && <p className="text-center"><em>No {field.gpnfEntryLabelPlural}</em></p>}
            {!disableEdit ?
                <div className="d-flex align-items-center">
                    {hasExcelUpload && <button type="button"
                                               className="btn btn-sm btn-secondary mr-2"
                                               onClick={() => {
                                                   setShowExcelUploadModal(true);
                                               }}
                    >
                        Excel Upload
                    </button>}
                    {hasCsvDownload && <button type="button"
                                               className="btn btn-sm btn-secondary mr-2"
                                               disabled={value.length === 0}
                                               onClick={() => {
                                                   handleCsvDownload(field, value);
                                               }}
                    >Download CSV</button>}
                    {!hideAddButton &&
                        <button type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                    setFormState({
                                        ...initialFormState,
                                        fieldState: {},
                                        fieldValues: initialFieldValues
                                    });
                                    setShowAddRecordModal(true);
                                }}
                        >Add {field.gpnfEntryLabelSingular}</button>
                    }
                    {additionalButtons && additionalButtons.map((item: JSX.Element) => item)}
                </div>
                :
                <div className="d-flex align-items-center">
                    {hasCsvDownload && <button type="button"
                                               className="btn btn-sm btn-secondary mr-2"
                                               disabled={value.length === 0}
                                               onClick={() => {
                                                   handleCsvDownload(field, value);
                                               }}
                    >Download CSV</button>}
                </div>
            }

            {showAddRecordModal &&
                <Modal size={addRecordModalSize}>
                    <div className="modal-header">
                        <h5 className="modal-title">Add {field.gpnfEntryLabelSingular}</h5>
                        <button type="button"
                                className="close"
                                aria-label="Close"
                                onClick={() => {
                                    setShowAddRecordModal(false);
                                }}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        {addRecordModalMessage?.show &&
                            <Alert
                                type={addRecordModalMessage?.type || 'success'}
                                message={addRecordModalMessage?.message || ''}
                                icon={addRecordModalMessage?.icon || false}
                                isDismissable={addRecordModalMessage?.isDismissable || false}
                                dismiss={() => {
                                    setAddRecordModalMessage({...initialAddRecordModalMessageState});
                                }}
                            />
                        }

                        <FormContainer>
                            <Form form={field.nestedForm}
                                  formState={formState}
                                  setFormState={setFormState}
                                  editableFieldIds={field?.gpnfEditableFields}
                                  displayOnly={modalLocalDisplayOnly || field.hasOwnProperty('gpnfEditableFields')}
                                  displayFields={field?.gpnfDisplayFields}
                                  displayFieldsMode={field?.gpnfDisplayFieldMode}
                                  showAdministrativeFields={field.hasOwnProperty('gpnfDisplayFields') || showAdministrativeFields}
                            />
                        </FormContainer>
                    </div>
                    <div className="modal-footer">
                        {modalLocalDisplayOnly ?
                            <button type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowAddRecordModal(false);
                                    }}
                            >Close
                            </button>
                            :
                            <>
                                <button type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowAddRecordModal(false);
                                        }}
                                >Cancel
                                </button>

                                <div className="btn-group dropup"
                                     ref={buttonDropdownRef}
                                >
                                    <ActivityButton
                                        isActive={formIsSubmitting}
                                        disabled={formIsBusy(formState)}
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setFormIsSubmitting(true);
                                            if (!handleSaveNestedFormEntry()) {
                                                return;
                                            }
                                            setFormIsSubmitting(false);
                                            setShowAddRecordModal(false);
                                        }}
                                    >Save {field.gpnfEntryLabelSingular}
                                    </ActivityButton>
                                    {saveOptions?.length > 0 &&
                                        <>
                                            <button type="button"
                                                    className="btn btn-primary dropdown-toggle dropdown-toggle-split"
                                                    aria-haspopup="true"
                                                    aria-expanded="false"
                                                    onClick={() => {
                                                        setIsShowingButtonMenu(!isShowingButtonMenu);
                                                    }}
                                            >
                                                <span className="sr-only">Toggle Dropdown</span>
                                            </button>
                                            {isShowingButtonMenu &&
                                                <>
                                                    <div className="dropdown-menu show">
                                                        {saveOptions.map((o: string) => {
                                                            const label = getLabelForSaveOption(o);

                                                            return (
                                                                <a className="dropdown-item"
                                                                   href="#"
                                                                   onClick={(e) => {
                                                                       e.stopPropagation();
                                                                       e.preventDefault();
                                                                       setIsShowingButtonMenu(false);

                                                                       setFormIsSubmitting(true);
                                                                       if (!handleSaveNestedFormEntry()) {
                                                                           return;
                                                                       }

                                                                       // backup current formstate
                                                                       const currentFormState: any = formState;

                                                                       // clear current formstate
                                                                       setFormState({
                                                                           ...initialFormState,
                                                                           fieldState: {},
                                                                           fieldValues: initialFieldValues
                                                                       });

                                                                       // display a saved message
                                                                       setAddRecordModalMessage({
                                                                           ...initialAddRecordModalMessageState,
                                                                           show: true,
                                                                           message: `${field.gpnfEntryLabelSingular} Saved`,
                                                                           isDismissable: true
                                                                       });

                                                                       // clear message after 2s
                                                                       setTimeout(() => {
                                                                           setAddRecordModalMessage({...initialAddRecordModalMessageState});
                                                                       }, 2000);

                                                                       if (o === 'save_and_new') {
                                                                           // nothing extra to do
                                                                       } else if (o === 'save_and_copy_new') {
                                                                           // copy values from previous formstate to current formstate
                                                                           const newFormStateFieldValues = Object.keys(currentFormState?.fieldValues).reduce((acc: any, key: string) => {
                                                                               if (key.match(/^input_[1-9]/)) {
                                                                                   return {
                                                                                       ...acc,
                                                                                       [key]: currentFormState.fieldValues[key]
                                                                                   };
                                                                               }
                                                                               return acc;
                                                                           }, {});

                                                                           setFormState({
                                                                               ...initialFormState,
                                                                               fieldState: {},
                                                                               fieldValues: newFormStateFieldValues
                                                                           });
                                                                       }

                                                                       setFormIsSubmitting(false);
                                                                   }}
                                                                >{label}</a>
                                                            )
                                                        })}
                                                    </div>
                                                </>
                                            }
                                        </>
                                    }
                                </div>
                            </>
                        }
                    </div>
                </Modal>}
            {showExcelUploadModal &&
                <NestedFormExcelUpload
                    field={field}
                    dismiss={() => {
                        setShowExcelUploadModal(false)
                    }}
                    onUploadComplete={(data: object[], meta: any) => {
                        onChange([
                            ...value,
                            ...data
                        ]);
                        setNestedFormExcelUploadErrorMessage(meta?.errorMessage || '');
                    }}
                />}
        </div>
    );
};